import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useGetOrganizationClientsQuery } from 'src/redux/organization/organization.endpoints';
import { useGetCompaniesQuery } from 'src/pages/billing/endpoints/billing.endpoints';
import {
  PortalDsButton,
  PortalDsInput,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import notificationService from 'src/services/notification/notification.service';
import ManageClientsTable from '../components/manage-clients/manage-clients-table';
import { Client } from '../types/client.types';
import { ManageClientDrawer } from '../components/manage-clients/manage-client-drawer';
import CreateClientModal from '../components/manage-clients/create-client-modal';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { useManageClientsUrlParams } from '../hooks/use-manage-clients-url-params';

const ManageClientsTab: React.FC = () => {
  const dispatch = useDispatch();
  const { setClient, clearClient } = useActiveClient();
  const {
    clientId,
    section,
    openClient,
    closeClient,
    setSection,
    clearClientParams,
  } = useManageClientsUrlParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const unresolvedClientNotifiedRef = useRef<string | null>(null);

  const {
    data: clients,
    isLoading: isLoadingClients,
    isFetching: isFetchingClients,
  } = useGetOrganizationClientsQuery();
  const { data: companies = [] } = useGetCompaniesQuery();

  const resolvedClient = useMemo(() => {
    if (!clientId || !clients) {
      return null;
    }
    return clients.find((client) => client.ext_client_id === clientId) ?? null;
  }, [clientId, clients]);

  const isResolvingClient = Boolean(
    clientId && !resolvedClient && (clients === undefined || isLoadingClients)
  );
  const isDrawerOpen = Boolean(clientId && resolvedClient);

  useEffect(() => {
    if (!clientId) {
      unresolvedClientNotifiedRef.current = null;
      clearClient();
      return;
    }

    if (resolvedClient) {
      setClient(resolvedClient);
      return;
    }

    if (clients && !resolvedClient) {
      if (unresolvedClientNotifiedRef.current !== clientId) {
        unresolvedClientNotifiedRef.current = clientId;
        clearClientParams();
        clearClient();
        notificationService.handleError(
          null,
          dispatch,
          'Client not found or you do not have access'
        );
      }
    }
  }, [
    clientId,
    clients,
    resolvedClient,
    clearClient,
    clearClientParams,
    setClient,
    dispatch,
  ]);

  const filteredClients = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const list = clients ?? [];
    if (!query) {
      return list;
    }
    return list.filter((client) => {
      const haystack = [
        client.title,
        client.website ?? '',
        ...(client.companies ?? []).map((company) => company.company_name),
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [clients, searchValue]);

  const handleClientSelect = (client: Client) => {
    openClient(client.ext_client_id);
  };

  const handleClientCreated = (client: Client) => {
    openClient(client.ext_client_id);
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      closeClient();
      clearClient();
    }
  };

  if (isResolvingClient) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2
          className="size-8 animate-spin text-[#5E6278]"
          aria-label="Loading client"
        />
      </div>
    );
  }

  if (isDrawerOpen) {
    return (
      <>
        <ManageClientDrawer
          open={isDrawerOpen}
          section={section}
          onSectionChange={setSection}
          onOpenChange={handleDrawerOpenChange}
        />
        <CreateClientModal
          open={showCreateModal}
          companies={companies}
          onOpenChange={setShowCreateModal}
          onSuccess={handleClientCreated}
        />
      </>
    );
  }

  return (
    <>
      <div className={cn(portalDs.page, 'h-full overflow-y-auto')}>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h1 className="text-[24px] font-bold tracking-[-0.5px] text-[#0D0F1A]">
              Clients
            </h1>
            <p className="mt-1 text-[13.5px] text-[#5E6278]">
              Manage access, reporting, and users for each client account.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#9CA3AF]" />
              <PortalDsInput
                value={searchValue}
                placeholder="Search clients..."
                className="h-10 pl-9"
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </div>
            <PortalDsButton
              variant="primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="size-4" />
              New client
            </PortalDsButton>
          </div>
        </div>

        <ManageClientsTable
          clients={filteredClients}
          loading={isLoadingClients || isFetchingClients}
          onClientSelect={handleClientSelect}
        />
      </div>

      <CreateClientModal
        open={showCreateModal}
        companies={companies}
        onOpenChange={setShowCreateModal}
        onSuccess={handleClientCreated}
      />
    </>
  );
};

export default ManageClientsTab;
